import { useEffect, useRef, useState } from 'react'
import { useLang } from '../lang'
import type { Bootstrap, Tab } from '../types'
import { AccountMenu } from './AccountMenu'
import * as s from './shell.css'

// The primary navigation — the four public surfaces (brief §3.1) plus About, which is back in the
// nav after a spell as a footer link: a station whose whole point is explaining itself should not
// hide the explanation. Sources & credits stays in the footer. icon = the phone fallback (the word
// is hidden below 560px, so a new entry needs a glyph that is in the icon subset — ti-info-circle
// still is; anything else needs bin/build-icon-subset re-run, guarded by icon_subset_spec).
const TABS: { id: Tab; en: string; ga: string; icon: string }[] = [
  { id: 'live', en: 'Live', ga: 'Beo', icon: 'ti-feather' },
  { id: 'journal', en: 'Journal', ga: 'Dialann', icon: 'ti-notebook' },
  { id: 'notes', en: 'Notes', ga: 'Nótaí', icon: 'ti-writing' },
  { id: 'stats', en: 'Stats', ga: 'Sonraí', icon: 'ti-chart-bar' },
  { id: 'directory', en: 'Directory', ga: 'Eolaí', icon: 'ti-book-2' },
  { id: 'about', en: 'About', ga: 'Fúinn', icon: 'ti-info-circle' },
]

interface MastheadProps {
  bootstrap: Bootstrap
  tab: Tab
  onTab: (t: Tab) => void
  onOpenAccount: () => void
}

export function Masthead({ bootstrap, tab, onTab, onOpenAccount }: MastheadProps) {
  const { lang, setLang, t } = useLang()
  // A station that reads no blog has no Notes tab — absence, not an empty section.
  const tabs = bootstrap.notes ? TABS : TABS.filter((entry) => entry.id !== 'notes')
  // Sticky + condenses once the page scrolls. Hysteresis (condense past 56, expand under 8)
  // stops the shortening header from oscillating across a single threshold.
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrolled((prev) => (prev ? window.scrollY > 8 : window.scrollY > 56))
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Publish the masthead's LIVE height as --mast-h. The header is not a fixed height — it condenses
  // on scroll and shifts with the mark size — so surfaces that dock below it (the Admin workspace,
  // `top: var(--mast-h)`) must read the real height, not a hardcoded guess, or they overlap the
  // wordmark. The 54px in colors.css stays only as a pre-mount fallback.
  const headerRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const publish = () => document.documentElement.style.setProperty('--mast-h', `${el.offsetHeight}px`)
    publish()
    const ro = new ResizeObserver(publish)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <header ref={headerRef} className={`${s.topbar}${scrolled ? ' ' + s.topbarScrolled : ''}`}>
      <div className={s.top}>
        <button className={s.brand} onClick={() => onTab('live')}>
          {bootstrap.assets.mark && (
            <img
              className={s.mark}
              src={bootstrap.assets.mark}
              alt={t(bootstrap.assets.mark_alt.en, bootstrap.assets.mark_alt.ga)}
              width={52}
              height={46}
            />
          )}
          <span className={s.word}>{bootstrap.site_name}</span>
        </button>
        <nav className={s.nav} aria-label={t('Sections', 'Rannóga')}>
          {tabs.map((item) => {
            const label = lang === 'ga' ? item.ga : item.en
            return (
              <button
                key={item.id}
                className={s.navItem}
                aria-current={tab === item.id ? 'page' : undefined}
                aria-label={label}
                onClick={() => onTab(item.id)}
              >
                <i className={`${s.navIcon} ti ${item.icon}`} aria-hidden="true" />
                <span className={s.navLabel}>{label}</span>
              </button>
            )
          })}
          <div className={s.lang} role="group" aria-label={t('Language', 'Teanga')}>
            <button className={s.langOpt} data-on={lang === 'en'} onClick={() => setLang('en')}>
              EN
            </button>
            <button className={s.langOpt} data-on={lang === 'ga'} onClick={() => setLang('ga')}>
              GA
            </button>
          </div>
          <AccountMenu user={bootstrap.current_user} onOpenAccount={onOpenAccount} />
        </nav>
      </div>
    </header>
  )
}
